// library imports
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Tooltip,
  Modal,
  Divider,
  Flex,
  Popconfirm,
  Spin,
  DatePicker,
} from "antd";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// function imports
import { useTableSearch } from "../../../functions/tableSearch";
// component imports
import DataTable from "../../../components/common/DataTable";
import PublicHolidaysForm from "./PublicHolidaysForm";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// hook imports
import useFetch from "../../../hooks/useFetch";

export default function PublicHolidaysView() {
  const [fetchWrapper] = useFetch();

  const { getColumnSearchProps } = useTableSearch();

  // index context
  const { notificationApi } = useContext(IndexContext);

  // loading states
  const [firstLoad, setFirstLoad] = useState(false);
  const [loading, setLoading] = useState(false); // loading for table

  // modal states
  const [isAddPublicHolidayModalOpen, setIsAddPublicHolidayModalOpen] = useState(false);
  const [isEditPublicHolidayModalOpen, setIsEditPublicHolidayModalOpen] = useState(false);
  const [selectedPublicHoliday, setSelectedPublicHoliday] = useState({});

  // table data
  const [publicHolidayDetails, setPublicHolidayDetails] = useState([]);

  // selection for year
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const publicHolidayTableColumns = [
    {
      title: "Remark",
      dataIndex: "Remark",
      key: "Remark",
      width: "20%",
      ...getColumnSearchProps("Remark"),
    },
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      width: "20%",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      width: "20%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                setIsEditPublicHolidayModalOpen(true);
                setSelectedPublicHoliday(record);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Holiday"
              description={"Are you sure to delete this Holiday?"}
              onConfirm={() => {
                deletePublicHoliday(record.ID);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get public holiday details */
  function getPublicHolidayDetails() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "public_holidays_management/getPublicHolidays",
      method: "GET",
      set_content_type_header: false,
      params: {
        selected_year: selectedDate.startOf("year").format("YYYY-MM-DD"),
      },

      onSuccess: (response) => {
        setPublicHolidayDetails(response.data);
      },

      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get Public Holidays.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
        setFirstLoad(false);
      },
    });
  }

  function deletePublicHoliday(public_holiday_id) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "public_holidays_management/deletePublicHoliday",
      body: {
        public_holiday_id: public_holiday_id,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getPublicHolidayDetails();
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to delete Public Holiday.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    getPublicHolidayDetails();
  }, [selectedDate]);

  return (
    <>
      {firstLoad ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <>
          <Flex>
            <DatePicker
              className="mb-5 mr-3"
              picker="year"
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
              }}
            />

            <Button
              icon={<PlusOutlined />}
              className="mb-5"
              onClick={() => setIsAddPublicHolidayModalOpen(true)}
            >
              New Holiday
            </Button>
          </Flex>

          {loading ? (
            <Flex justify="center" align="center" style={{ height: "50vh" }}>
              <Spin />
            </Flex>
          ) : (
            <DataTable
              table_column={publicHolidayTableColumns}
              table_data={publicHolidayDetails}
            />
          )}
        </>
      )}

      <Modal
        title="Add Public Holiday"
        open={isAddPublicHolidayModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <PublicHolidaysForm
          mode="add"
          setIsPublicHolidaysFormModalOpen={setIsAddPublicHolidayModalOpen}
          refreshTable={getPublicHolidayDetails}
          selectedYear={selectedDate}
        />
      </Modal>

      <Modal
        title="Edit Public Holiday"
        open={isEditPublicHolidayModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <PublicHolidaysForm
          mode="edit"
          setIsPublicHolidaysFormModalOpen={setIsEditPublicHolidayModalOpen}
          isPublicHolidaysForm={false}
          refreshTable={getPublicHolidayDetails}
          initialValues={selectedPublicHoliday}
          selectedYear={selectedDate}
        />
      </Modal>
    </>
  );
}
